import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseDashboard } from '../../models/dashboard/BaseDashboard';
import { FormSearch } from '../../models/dashboard/FormStatus';
import { UtilityService } from '../utility.service';
import { TasksByAccountItem, TasksByAccountsBatchResultItem } from '../../models/projects-grants/TasksByAccountsBatch';
import { BatchCacheLoader } from '../../shared/utils/batch-cache-loader';
import { DynamicFormUtilityServices } from '../dynamic-form-utility.service';

@Injectable({
	providedIn: 'root',
})
export class ProjectGrantTaskService {
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });
	private readonly urlPath = `${environment.apiUrl}project-grant/clients/`;

	// Per (clientId, isActive) DataLoader that coalesces same-tick tasks-by-account lookups into one
	// batch request and memoizes by `projectGrantId|account` for the page lifecycle. Task reference data
	// is stable within an editing session, so memoizing is safe.
	private readonly taskLoaders = new Map<string, BatchCacheLoader<unknown[]>>();

	constructor(
		private httpClient: HttpClient,
		private utilityService: UtilityService,
		private dynamicFormUtilityServices: DynamicFormUtilityServices
	) {}

	private getTaskLoader(clientId: string, isActive: boolean): BatchCacheLoader<unknown[]> {
		const scope = `${clientId}|${isActive}`;
		let loader = this.taskLoaders.get(scope);
		if (!loader) {
			loader = new BatchCacheLoader<unknown[]>(
				(keys) =>
					firstValueFrom(
						this.getTasksByAccountsBatch(
							clientId,
							keys.map((key) => {
								const separatorIndex = key.indexOf('|');
								return {
									key,
									projectGrantId: key.substring(0, separatorIndex),
									account: key.substring(separatorIndex + 1),
									// The single-item endpoint feeds the caller's `isActive` value straight into the
									// server's `IsClosed` filter (x.IsClosed == isActive), so the batch must pass the
									// same value through unchanged to preserve behaviour — do NOT negate it.
									isClosed: isActive,
								};
							})
						).pipe(map((items) => new Map(items.map((item) => [item.key, item.tasks]))))
					),
				// A batch omission resolves like the single-item endpoint's "not found": empty.
				{ defaultForMissingKey: () => [] }
			);
			this.taskLoaders.set(scope, loader);
		}
		return loader;
	}

	/** Drop the memoized task lookups (e.g. on client switch or after config edits). */
	clearTaskLookupCache(): void {
		this.taskLoaders.forEach((loader) => loader.clear());
		this.taskLoaders.clear();
	}

	createForm(clientId: string, request: any, prefix: string = ''): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-task`, this.setupRequest(request, prefix))
			.pipe(tap(() => this.clearTaskLookupCache()));
	}

	private setupRequest(request: any, prefix: string = '') {
		const formData: FormData = new FormData();

		formData.append('model', JSON.stringify(request));
		if (request.additionalField?.dynamicFormConfigurations) {
			this.dynamicFormUtilityServices.appendDocuments(formData, prefix, request);
		}
		return formData;
	}

	updateForm(clientId: string, id: string, request: any, prefix: string = ''): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/project-grant-task/${id}`, this.setupRequest(request, prefix))
			.pipe(tap(() => this.clearTaskLookupCache()));
	}

	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/project-grant-task/${id}`).pipe();
	}

	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-task/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		// Approval makes a task visible to the by-account lookups.
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-task/${id}/approve`, {
				userId,
			})
			.pipe(tap(() => this.clearTaskLookupCache()));
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		let url = `${this.urlPath}${clientId}/project-grant-task/users/${userId}?${this.utilityService.objectToQueryString(
			search
		)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient.post<any>(`${this.urlPath}${clientId}/project-grant-task/${id}/review`, {}).pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest('POST', `${this.urlPath}${clientId}/project-grant-task/${id}/docs`, formData, {
			reportProgress: true,
			responseType: 'json',
		});
		return this.httpClient.request(req);
	}

	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(`${this.urlPath}${clientId}/project-grant-task/${formId}/docs/${docId}`);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/project-grant-task/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/project-grant-task/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	getAllTask(clientId: string, projectGrantId: string, isClosed: boolean): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-task/get-all-task/${projectGrantId}/${isClosed}`, {
				headers: this.headers,
			})
			.pipe();
	}

	getProjectsAllTasks(clientId: string, isClosed: boolean): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-task/get-projects-all-tasks/${isClosed}`, {
				headers: this.headers,
			})
			.pipe();
	}

	getRollforward(clientId: string, projectGrantId: string, year: number, month: number): Observable<any[]> {
		return this.httpClient
			.get<any[]>(`${this.urlPath}${clientId}/project-grant-task/rollforward/${projectGrantId}/${year}/${month}`)
			.pipe();
	}
	getTaskTransactionDetail(clientId: string, taskId: string, year: number, month: number): Observable<any> {
		return this.httpClient
			.get<any>(`${this.urlPath}${clientId}/project-grant-task/task-transaction-detail/${taskId}/${year}/${month}`)
			.pipe();
	}
	getTasksByAccount(
		clientId: string,
		projectGrantId: string,
		account: string,
		isActive: boolean = true
	): Observable<any[]> {
		if (!projectGrantId || !account || account.trim() === '') {
			return of([]);
		}
		// Routed through the batch+cache loader: identical/same-tick lookups coalesce to a single
		// get-all-by-accounts request and repeats are served from cache. The composite key keeps each
		// (projectGrant, account) pair distinct; the result maps back byte-identical to the old GET.
		const key = `${projectGrantId}|${account.trim()}`;
		return this.getTaskLoader(clientId, isActive)
			.load$(key)
			.pipe(map((tasks) => (tasks as any[]) ?? []));
	}

	// Batch of getTasksByAccount: resolves tasks for many (projectGrant, account, isClosed) rows in one
	// request. Each item carries a correlation key echoed back so results map to rows without ordering.
	getTasksByAccountsBatch(clientId: string, items: TasksByAccountItem[]): Observable<TasksByAccountsBatchResultItem[]> {
		return this.httpClient
			.post<TasksByAccountsBatchResultItem[]>(`${this.urlPath}${clientId}/project-grant-task/get-all-by-accounts`, {
				items,
			})
			.pipe();
	}
}
